import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import { differenceInYears } from 'date-fns'
import { isNil } from 'ramda'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'
import {
  type CheckoutCarRentalService,
  type CheckoutCarRentalServiceOption,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { getGA4CategoryByServiceType } from '../model-mappers/ga4AnalyticsMapper'
import {
  createServiceCarRentalOptionMapper,
  type ServiceCarRentalOptionMapper,
} from '../model-mappers/serviceCarRentalOptionMapper'
import {
  APIBookingDraft,
  APIBookingDraftCarRentalService,
  APIBookingDraftCarRentalServiceOption,
  APIBookingDraftRoom,
} from '../types'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceOptionMapper?: ServiceCarRentalOptionMapper
  serviceType: ServiceType
}

const MIN_AGE_DRIVER = 18
const RENTAL_CONDITIONS_MIN_AGE_DRIVER = 25
const RENTAL_CONDITIONS_MAX_AGE_DRIVER = 69

function isAtLeastMinAge(birthdate: string | undefined, minAge: number): boolean {
  if (!birthdate)
    return true

  const now = new Date()
  const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const birthdateUtc = new Date(`${birthdate}T00:00:00.000Z`)

  if (Number.isNaN(birthdateUtc.getTime()))
    return false

  return differenceInYears(todayUtc, birthdateUtc) >= minAge
}

export const createCarRentalFilteringServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutCarRentalService> = (dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceCarRentalOptionMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftCarRentalService

    if (!service)
      return null
    const { aName, options, usps } = service
    const carLists = mapCarLists(options, service) ?? []
    const rooms = mapRoomsForCars(options, bookingDraft.rooms)

    const anyCarSelected = carLists.some(({ selectedValue }) => selectedValue)

    const participants
      = bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, options),
            selectedId:
              options
                .filter(({ isSelected }) => isSelected)
                .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))
                ?.uniqueId,
          },
        }),
        {},
      ) ?? {}

    const adultParticipants = bookingDraft?.participants?.filter(participant => participant.ageCategory === 'Adult') ?? []
    const eligibleDrivers = adultParticipants.filter(participant =>
      isAtLeastMinAge(participant.birthdate, MIN_AGE_DRIVER),
    )

    return {
      id: aName,
      isSelected: anyCarSelected ?? !isNil(anyCarSelected),
      type: ServiceType.CarRentalFiltering,
      usps: usps?.map(usp => ({ link: usp.link, text: usp.text })) ?? [],
      maxNumbersOfCarsToBeBooked: service.serviceBookingLimit ?? 1,
      isOpenJawPackage: service.isOpenJawPackage ?? false,
      includedCarOutOfStock: service.includedCarOutOfStock ?? false,
      includedCarPriceChanged: service.includedCarPriceChanged ?? false,
      carProviderIsOutOfService: service.carProviderIsOutOfService ?? false,
      hasUnderageAdults: eligibleDrivers.length < adultParticipants.length,
      carLists,
      options: options.reduce(
        (acc2: Record<string, CheckoutCarRentalServiceOption>, option) => ({
          ...acc2,
          [getUniqOptionIdFromOption(option) ?? '']: serviceOptionMapper.map(option, service),
        }),
        {},
      ),
      participants,
      rooms,
      availableDrivers: eligibleDrivers.map(({ id }) => id.toString()),
      configurations: {
        minAgeDriver: MIN_AGE_DRIVER,
        rentalConditionsMinAgeDriver: RENTAL_CONDITIONS_MIN_AGE_DRIVER,
        rentalConditionsMaxAgeDriver: RENTAL_CONDITIONS_MAX_AGE_DRIVER,
      },
    }
  }

  function getUniqOptionIdFromOption(option: APIBookingDraftCarRentalServiceOption | undefined) {
    if (!option)
      return undefined
    return buildUniqOptionCarId(option.uniqueId, option.code)
  }

  function mapCarLists(options: APIBookingDraftCarRentalServiceOption[], service: APIBookingDraftCarRentalService) {
    const selectedCars = options.filter(({ isSelected }) => isSelected)

    return selectedCars.map((selectedCar) => {
      const availableOptionsToUpgrade = options
        .filter(({ isSelected }) => !isSelected)
        .filter(({ code }) => code.split('??')[0] !== selectedCar.code.split('??')[0])
        .filter(({ uniqueId }) => uniqueId !== selectedCar.uniqueId)
        .map((option) => {
          return serviceOptionMapper.mapUpgradeOption(option, service, selectedCar.price)
        })

      return {
        selectedValue: selectedCar?.code,
        selectedId: selectedCar?.uniqueId ?? '',
        driverId: selectedCar?.driverId?.toString() ?? '',
        availableOptionsToUpgrade,
      }
    })
  }

  function mapRoomsForCars(
    options: APIBookingDraftCarRentalServiceOption[],
    rooms: APIBookingDraftRoom[],
  ) {
    const selectedOptions = options.filter(({ isSelected }) => isSelected)

    const sameParticipants = (a: number[], b: number[]) =>
      a.length === b.length && a.every(id => b.includes(id))

    return rooms.reduce<Record<string, {
      name: string
      id: string
      participantIds: string[]
      selectedValue?: string
    }>>((acc, room) => {
      const matchingOption = selectedOptions.find(({ assignedParticipants }) =>
        sameParticipants(assignedParticipants, room.participantIds),
      )

      acc[room.id.toString()] = {
        name: room.name,
        id: room.id.toString(),
        participantIds: room.participantIds.map(id => id.toString()),
        selectedValue: matchingOption?.uniqueId,
      }

      return acc
    }, {})
  }

  function mapSelection(service: CheckoutCarRentalService) {
    const { participants, availableDrivers, carLists } = service
    return (availableDrivers ?? []).map((id) => {
      const { birthdate } = participants[id]

      const currentValue = carLists?.find(({ driverId }) => driverId === id)?.selectedValue ?? null
      const currentId = carLists?.find(({ driverId }) => driverId === id)?.selectedId ?? null
      return {
        participantId: id,
        value: currentValue ?? null,
        id: currentId ?? null,
        driverBirthDate: birthdate,
        equipmentsWithQuantity: currentValue
          ? service.options[`${currentId}-${currentValue}`].selectedEquipmentsWithQuantity
          : [],
      }
    })
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, _config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftCarRentalService | undefined
    if (!service)
      return []

    return service.options
      .map(option => ({
        item_name: option.displayText,
        item_id: option.code,
        price: serviceOptionMapper.map(option, service).price,
        item_category: `Ancillary > Additional cost > ${getGA4CategoryByServiceType(serviceType, service.aName)}`,
        item_category2: getGA4CategoryByServiceType(serviceType, service.aName),
        item_list_name: 'checkout1',
        quantity: '1',
      }))
  }

  function mapSelectedItemToGA4(service: CheckoutCarRentalService, step: number): ImpressionViewAncillariesItem[] {
    const selectedCars = (service.carLists ?? []).reduce<{ inputs: SelectedItemInput[], options: CheckoutCarRentalService['options'][string][] }>(
      (acc, carList) => {
        if (!carList.selectedValue || !carList.selectedId)
          return acc

        const key = buildUniqOptionCarId(carList.selectedId, carList.selectedValue)
        const option = service.options[key]
        if (!option)
          return acc

        acc.inputs.push({
          label: option.carClass || '',
          value: key,
          price: option.price,
        })
        acc.options.push(option)

        return acc
      },
      { inputs: [], options: [] },
    )

    return mapDefaultSelectedItemToGA4(selectedCars.inputs, {
      serviceType,
      serviceId: service.id,
      step,
    }).map((item, index) => ({
      ...item,
      item_category: `Ancillary > Additional cost > ${selectedCars.options[index].value}`,
      item_category2: selectedCars.options[index].value,
    }))
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}

export function buildUniqOptionCarId(id: string, code: string) {
  return `${id}-${code}`
}
